import React, { useRef, useState, useCallback, useEffect, useReducer } from 'react';
import ReactFlow, {
    ReactFlowProvider,
    addEdge,
    removeElements,
    Controls,
    Background,
    updateEdge,
} from 'react-flow-renderer';

import { useAudio } from '@h';

import { Sidebar } from './Sidebar';
import { nodeTypes } from './Node';
import { edgeTypes } from './Edge';
import { ModalProvder } from './Modal';
import { historyReducer, initialHistory } from './history';

import katex from 'katex';
import 'katex/dist/katex.min.css';
import { toClassNames } from '@helps';
import RaactModal from 'react-modal'
import styles from './style.module.scss';
import { Loader } from '..';


// RaactModal.setAppElement('#root');

window.katex = katex;

const _initElements = [
    {
        id: '1',
        type: 'start',
        position: {
            x: 320,
            y: 320,
        },
        data: {},
    },
    {
        id: '2',
        type: 'text',
        position: {
            x: 680,
            y: 320,
        },
        data: {
            text: '<p>Добро пожаловать {firstname} на электронное занятие по теме {theme}, сегодня {data}.</p>',
            subject: '',
            images: [],
            videos: [],
            audios: [],
        },
    },
    {
        id: '3',
        type: 'finish',
        position: {
            x: 1280,
            y: 320,
        },
        data: {},
    },
    {
        source: '1',
        sourceHandle: 'out',
        target: '2',
        targetHandle: 'in',
        type: 'customedge',
        data: {},
        id: 'reactflow__edge-1out-2in',
    },
    {
        source: '2',
        sourceHandle: 'out',
        target: '3',
        targetHandle: 'in',
        type: 'customedge',
        data: {},
        id: 'reactflow__edge-2out-3in',
    },
];

const customStyles = {
    content: {
      display: 'flex',
      flexDirection: 'column',
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
    },
  }

const toSerializable = (data = {}) => {
    if (!data || typeof data !== 'object') return data;

    return Object.keys(data).reduce((acc, key) => {
        if (typeof data[key] !== 'function') {
            acc[key] = data[key];
        }
        return acc;
    }, {});
};

const Constructor = ({
    initElements = _initElements,
    disabled = false,
    gendisabled = false,
    onSave = () => {},    
    onGenerate = () => {},     
    onPDF = () => {},
    _onRegenerate = () =>  {},   
    className = '',
    style = {},
    onReviews = () => {},
}) => {
    const reactFlowWrapper = useRef(null);

    const [reactFlowInstance, setReactFlowInstance] = useState(null);

    const [history, dispatchHistory] = useReducer(historyReducer, [], initialHistory);
    const elements = history.present;

    const idRef = useRef(0);
    const [isPopup, setIsPopup] = useState(false);
    const [isComment, setComment] = useState('');
    const [isForce, setForce] = useState(false);
    const [confirm, setConfirm] = useState(null);

    const elementsRef = useRef(elements);
    const pendingInitIds = useRef(new Set());

    const { initAudio } = useAudio();

    useEffect(() => {
        initAudio();
    }, []);

    useEffect(() => {
        elementsRef.current = elements;
    }, [elements]);

    const onLoad = useCallback((_instance) => setReactFlowInstance(_instance), []);

    const commit = useCallback(
        (updater) => dispatchHistory({ type: 'SET', payload: updater }),
        []
    );

    const commitSilent = useCallback(
        (updater) => dispatchHistory({ type: 'SET_SILENT', payload: updater }),
        []
    );

    const undo = useCallback(() => dispatchHistory({ type: 'UNDO' }), []);
    const redo = useCallback(() => dispatchHistory({ type: 'REDO' }), []);
    const resetHistory = useCallback(
        (nextElements) => dispatchHistory({ type: 'RESET', payload: nextElements }),
        []
    );

    const getId = useCallback(() => {
        idRef.current += 1;
        return `${idRef.current}`;
    }, []);

    const onChange = useCallback(
        (id, _data) => {
            const stringId = `${id}`;
            const isPendingInit = pendingInitIds.current.delete(stringId);

            const updater = (prev) => {
                const idx = prev.findIndex((el) => `${el.id}` === stringId);
                if (idx === -1) return prev;

                const prevNode = prev[idx];
                const prevData = toSerializable(prevNode.data);
                const nextData = toSerializable(_data);

                if (JSON.stringify(prevData) === JSON.stringify(nextData)) {
                    return prev;
                }

                const tmp = [...prev];
                tmp[idx] = { ...prevNode, data: _data };
                return tmp;
            };

            if (isPendingInit) {
                commitSilent(updater);
            } else {
                commit(updater);
            }
        },
        [commit, commitSilent]
    );

    const onRemove = useCallback(
        (id) => {
            setConfirm({
                title: 'Удалить блок?',
                message:
                    'Блок и связанные с ним связи будут удалены. Вы сможете отменить это действие (Ctrl+Z).',
                confirmLabel: 'Удалить',
                onConfirm: () => {
                    commit((prev) => {
                        const elem = prev.find((el) => el.id === id);
                        return elem ? removeElements([elem], prev) : prev;
                    });
                    setConfirm(null);
                },
            });
        },
        [commit]
    );

    const onRegenerate = useCallback(
        (id) => {
            _onRegenerate({ id, scheme: elementsRef.current });
        },
        [_onRegenerate]
    );

    const onEdgeRemove = useCallback(
        (id) => {
            commit((prev) => {
                const elem = prev.find((el) => el.id === id);
                return elem ? removeElements([elem], prev) : prev;
            });
        },
        [commit]
    );

    const onDrop = (event) => {
        event.preventDefault();

        const reactFlowBounds =
            reactFlowWrapper.current.getBoundingClientRect();
        const type = event.dataTransfer.getData('application/reactflow');
        const position = reactFlowInstance.project({
            x: event.clientX - reactFlowBounds.left,
            y: event.clientY - reactFlowBounds.top,
        });

        const newNode = {
            id: getId(),
            type,
            position,
            data: {
                onChange,
                onRemove,
                onRegenerate,
            },
        };

        pendingInitIds.current.add(newNode.id);

        commit((prev) => (prev.length === 0 ? [newNode] : [...prev, newNode]));
    };

    const _onPDF = useCallback(() => {
        onPDF(1)
    }, [onPDF]);

    const _onCancelGenerate = useCallback(() => {
        setIsPopup(false);
        setComment('');
        setForce(false);
    }, []);

    const clearTable = useCallback(() => commit([]), [commit]);

    const _onGenerate = useCallback(
        (isComment, isForce) => {
            clearTable();
            gendisabled = true;
            const payload = { comment: isComment, force: isForce };
            onGenerate(payload);
            setIsPopup(false);
            setForce(false);
            setComment('');
        },
        [clearTable, onGenerate]
    );

    const clearLesson = useCallback(() => {
        setConfirm({
            title: 'Очистить урок?',
            message:
                'Все блоки и связи будут удалены. Вы сможете отменить это действие (Ctrl+Z).',
            confirmLabel: 'Очистить',
            onConfirm: () => {
                commit([]);
                setConfirm(null);
            },
        });
    }, [commit]);

    const onDragOver = (event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    };

    const onElementsRemove = useCallback(
        (elementsToRemove) => {
            if (!elementsToRemove || elementsToRemove.length === 0) return;

            setConfirm({
                title: 'Удалить выбранные элементы?',
                message: `Будет удалено элементов: ${elementsToRemove.length}. Вы сможете отменить это действие (Ctrl+Z).`,
                confirmLabel: 'Удалить',
                onConfirm: () => {
                    commit((prev) => removeElements(elementsToRemove, prev));
                    setConfirm(null);
                },
            });
        },
        [commit]
    );

    const onConnect = useCallback(
        (params) => {
            commit((prev) =>
                !prev.find(
                    (el) =>
                        el.sourceHandle === params.sourceHandle &&
                        el.source === params.source
                )
                    ? addEdge(
                          { ...params, type: 'customedge', data: { onEdgeRemove } },
                          prev
                      )
                    : prev
            );
        },
        [commit, onEdgeRemove]
    );

    const onEdgeUpdate = useCallback(
        (old, _new) => {
            commit((prev) => updateEdge(old, _new, prev));
        },
        [commit]
    );

    const onNodeDragStop = useCallback(
        (event, node) => {
            commitSilent((prev) =>
                prev.map((el) =>
                    el.id === node.id ? { ...el, position: node.position } : el
                )
            );
        },
        [commitSilent]
    );

    const _onSave = useCallback(() => {
        if (reactFlowInstance) {
            let start = 0;
            let finish = 0;

            let sourceCount = 0;

            const edges = [];

            const elems = reactFlowInstance.toObject().elements.map((el) => {
                if (el.type === 'start') start++;
                if (el.type === 'finish') finish++;

                if (el.type === 'customedge') edges.push(el);
                else {
                    switch (el.type) {
                        case 'question':
                        case 'questionWithTypes':
                            sourceCount += 2;
                            break;
                        case 'finish':
                            break;
                        default:
                            sourceCount += 1;
                            break;
                    }
                }

                const tmp = {};
                Object.keys(el.data).map(
                    (key) =>
                        typeof el.data[key] !== 'function' &&
                        (tmp[key] = el.data[key])
                );
                el.data = tmp;
                return el;
            });

            if (start !== 1) {
                alert('У курса должен быть блок начала!\nСохранение прервано');
                return;
            }
            if (finish !== 1) {
                alert('У курса должен быть блок конца!\nСохранение прервано');
                return;
            }

            if (edges.length === sourceCount) onSave(elems);
            else alert('Все блоки должны быть соеденины!\nСохранение прервано');
        }
    }, [reactFlowInstance, onSave]);

    useEffect(() => {
        const onKeyDown = (event) => {
            if (confirm || isPopup) return;

            const target = event.target;
            if (
                target &&
                (target.tagName === 'INPUT' ||
                    target.tagName === 'TEXTAREA' ||
                    target.isContentEditable)
            ) {
                return;
            }

            const mod = event.ctrlKey || event.metaKey;
            if (!mod) return;

            const key = event.key.toLowerCase();

            if (key === 'z' && event.shiftKey) {
                event.preventDefault();
                redo();
            } else if (key === 'z') {
                event.preventDefault();
                undo();
            } else if (key === 'y') {
                event.preventDefault();
                redo();
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [confirm, isPopup, undo, redo]);

    useEffect(() => {
        let _id = 0;
        const init =
            Array.isArray(initElements) && initElements.length > 0
                ? initElements
                : _initElements;

        const tmp = init.map((el) => {
            const tmpEl = { ...el };
            if (Object.keys(nodeTypes).indexOf(tmpEl.type) !== -1) {
                const parsedId = parseInt(tmpEl.id);
                if (!Number.isNaN(parsedId) && parsedId > _id) _id = parsedId;
                tmpEl.data = {
                    ...tmpEl.data,
                    onChange,
                    onRemove,
                    onRegenerate,
                };
            } else {
                tmpEl.data = {
                    ...tmpEl.data,
                    onEdgeRemove,
                };
            }

            return tmpEl;
        });

        idRef.current = _id;
        resetHistory(tmp);
    }, [initElements, onChange, onRemove, onRegenerate, onEdgeRemove, resetHistory]);

    return (
        <ModalProvder>
            {disabled && (
                <div className={styles.loader_background}>
                    <Loader color="#007aff" />
                </div>
            )}

                <RaactModal 

                    isOpen={isPopup} style={customStyles} contentLabel='Генерировать'>
                      <h1 className={styles.title}>Напишите требования к созданию уроков</h1>               
                      <textarea 
                        className={styles.textarea}
                        type='text' onChange={(e) => setComment(e.target.value)} value={isComment}  />
                    <p>  <p><br></br>
                        <label >
                        <input 
                    type="checkbox" 
                   
                    onChange={(e) => setForce(e.target.checked)} 
                    checked={isForce} 
                />&nbsp;
                Перезаписать старый урок</label></p>
                    </p>
                    <p><button onClick={() => _onCancelGenerate(isComment,isForce)} className={styles.save}>Отмена</button>&nbsp;                    
                    <button onClick={() => _onGenerate(isComment,isForce)} className={styles.save}>Генерировать</button>
                    </p>
                </RaactModal>

                <RaactModal
                    isOpen={!!confirm}
                    style={customStyles}
                    contentLabel="Подтверждение"
                >
                    <h1 className={styles.title}>{confirm?.title || 'Подтверждение'}</h1>
                    <p className={styles.confirm_message}>{confirm?.message}</p>
                    <p>
                        <button onClick={() => setConfirm(null)} className={styles.save}>
                            Отмена
                        </button>
                        &nbsp;
                        <button
                            onClick={() => confirm?.onConfirm && confirm.onConfirm()}
                            className={styles.save}
                        >
                            {confirm?.confirmLabel || 'ОК'}
                        </button>
                    </p>
                </RaactModal>
            
            <div
                className={toClassNames(className)}
                style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    ...style,
                }}
            >
                <ReactFlowProvider>
                    <div
                        ref={reactFlowWrapper}
                        style={{ width: '100%', backgroundColor: '#F7F7F7' }}
                    >
                        <ReactFlow
                            onLoad={onLoad}
                            elements={elements}
                            onDrop={onDrop}
                            onDragOver={onDragOver}
                            nodeTypes={nodeTypes}
                            onElementsRemove={onElementsRemove}
                            onConnect={onConnect}                           
                            edgeTypes={edgeTypes}
                            onEdgeUpdate={onEdgeUpdate}
                            onNodeDragStop={onNodeDragStop}
                            snapToGrid={true}
                            snapGrid={[40, 40]}
                        >
                            <Controls />
                            <Background />
                        </ReactFlow>
                    </div>
                    <Sidebar
                        onSave={_onSave}
                        onCancelGenerate={_onCancelGenerate}
                        onGenerate={_onGenerate}
                        onClear={clearLesson}
                        onPDF={_onPDF}
                        disabled={disabled}
                        gendisabled={gendisabled}
                        onReviews={onReviews}
                        setIsPopup={setIsPopup}
                    />
                </ReactFlowProvider>
            </div>
        </ModalProvder>
    );
};

export { Constructor };
